import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { ArrowDownZA, ArrowUpAZ, CalendarArrowDown, CalendarArrowUp, TrendingDown, TrendingUp } from "lucide-react";
import { getPatientNameMatchScore } from "./patientNameMatching";

export const capitalizePronoun = (pronoun) => {
  return pronoun
    .split("/")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("/");
};

export const calculateAge = (dob) => {
  const birthDate = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDifference = today.getMonth() - birthDate.getMonth();
  if (monthDifference < 0 || (monthDifference === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
};

export const getSortIcon = (field, sortField, sortDirection) => {
  if (sortField !== field) return null;

  if (field === "name") {
    return sortDirection === "asc" ? <ArrowUpAZ size={12} className="sortable-header-icon" /> : <ArrowDownZA size={12} className="sortable-header-icon" />;
  } else if (field === "date") {
    return sortDirection === "asc" ? <CalendarArrowUp size={12} className="sortable-header-icon" /> : <CalendarArrowDown size={12} className="sortable-header-icon" />;
  }
  return null;
};

export const getTrendIcon = (trend) => {
  switch (trend) {
    case "getting better":
      return (
        <Tooltip title="Getting better" arrow>
          <span className="severity-trend-chip severity-trend-chip-improving">
            <TrendingDown size={10} />
          </span>
        </Tooltip>
      );
    case "getting worse":
      return (
        <Tooltip title="Getting worse" arrow>
          <span className="severity-trend-chip severity-trend-chip-worsening">
            <TrendingUp size={10} />
          </span>
        </Tooltip>
      );
    case "trend unchanged":
    case "staying stable":
      return <span className="severity-trend-chip severity-trend-chip-stable">stable</span>;
    default:
      return null;
  }
};

export const sortPatients = (patients, sortField, sortDirection, searchQuery = "") => {
  return patients
    .filter((patient) => getPatientNameMatchScore(patient, searchQuery) >= 0)
    .sort((a, b) => {
      if (sortField === "name") {
        const comparison = a.lastName.localeCompare(b.lastName);
        return sortDirection === "asc" ? comparison : -comparison;
      } else if (sortField === "date") {
        const dateA = a.document?.documentDate || "";
        const dateB = b.document?.documentDate || "";
        const comparison = dateA.localeCompare(dateB);
        return sortDirection === "asc" ? comparison : -comparison;
      }
      return a.lastName.localeCompare(b.lastName);
    });
};

export const renderPatientTags = (patient, flags, tagWeight) => {
  if (!patient.document || !flags?.showTags || !Array.isArray(patient.document.tags) || patient.document.tags.length === 0) {
    return null;
  }

  return patient.document.tags
    .filter((tag) => tag.weight > tagWeight)
    .map((tag, index) => (
      <Tooltip key={index} title="Recent session topics" arrow>
        <span className="tag-box">{tag.tag.charAt(0).toUpperCase() + tag.tag.slice(1)}</span>
      </Tooltip>
    ));
};

export const renderRiskBadge = (patient, flags) => {
  if (!flags?.showSeverity || !patient.document?.risk_assessment?.riskLevel) return null;

  const riskLevel = patient.document.risk_assessment.riskLevel;
  const badgeClass = riskLevel === "low" ? "severity-bg-3" : 
                     riskLevel === "significant" ? "severity-bg-5" : "severity-bg-1";

  return (
    <Tooltip title="Latest risk level" arrow>
      <span className={`risk-level-badge ${badgeClass}`}>
        {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)} risk
      </span>
    </Tooltip>
  );
};

export const renderHistorySummary = (patient) => {
  if (!patient.document?.history_summaries) return "";
  
  const historySummaryEntry = Object.values(patient.document.history_summaries)
    .find((entry) => entry.originalName === "history_summary");
  
  return historySummaryEntry?.summary || "";
};

export const handleSort = (field, sortField, sortDirection, setSortField, setSortDirection) => {
  if (sortField === field) {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
  } else {
    setSortField(field);
    setSortDirection("asc");
  }
};
